import { Dispatch, useCallback } from 'react';
import {
  FileSelection,
  FileSelectionAction,
  FileSelectionState,
} from '@asset-metrix/portal-ui';
import { JsonSchema } from '@sagold/react-json-editor';
import { useQueryClient } from '@tanstack/react-query';

import { fetchDocUploadAIMetadataValues } from '../../../api/portalGateway/docviewUploadFormValues';
import { useDocUploadStore } from '../store/store';

const BATCH_SIZE = 12;

export type FetchAIMetadataForAllFilesParams = {
  contentId: string;
  fileStateDispatch: Dispatch<FileSelectionAction>;
  fileState: FileSelectionState;
};

export type FetchAIMetadataForAllFilesCallback = (
  filterParams: Record<string, string | string[] | null>,
  schema: JsonSchema | undefined,
  editorDataChangeHandler: (
    fileId: string,
    data: Record<string, unknown>
  ) => void,
  setAiExtractionExplanations: (
    fileId: string,
    data: Record<string, string | undefined>
  ) => void
) => Promise<void>;

/**
 * Hook that provides a function to fetch AI-extracted metadata for all PDF files.
 * Processes files in batches and updates file state during the fetch process.
 */
export const useFetchAIMetadataForAllFiles = ({
  contentId,
  fileStateDispatch,
  fileState,
}: FetchAIMetadataForAllFilesParams): {
  fetchAIMetadataForAllFiles: FetchAIMetadataForAllFilesCallback;
} => {
  const queryClient = useQueryClient();
  const { setAIMetadataExtractionStatus } = useDocUploadStore();

  const fetchAIMetadataForAllFiles = useCallback(
    async (
      filterParams: Record<string, string | string[] | null>,
      schema: JsonSchema | undefined,
      editorDataChangeHandler: (
        fileId: string,
        data: Record<string, unknown>
      ) => void,
      setAiExtractionExplanations: (
        fileId: string,
        data: Record<string, string | undefined>
      ) => void
    ) => {
      const fetchAIExtractedMetadata = async (fileData: FileSelection) => {
        const result = await queryClient.fetchQuery({
          queryKey: [
            `tableAIModalValues:${fileData.id}`,
            {
              contentId,
              file: fileData.file,
              userSelection: JSON.stringify({
                ...filterParams,
              }),
            },
          ],
          queryFn: fetchDocUploadAIMetadataValues,
        });
        return result || {};
      };

      const pdfFiles = fileState.filter(
        (state) => state.file.type === 'application/pdf'
      );

      // Process files in batches
      for (let i = 0; i < pdfFiles.length; i += BATCH_SIZE) {
        const batch = pdfFiles.slice(i, i + BATCH_SIZE);

        // Process each file in the batch concurrently
        await Promise.all(
          batch.map(async (file) => {
            // Update state to isFetchingMetadata
            fileStateDispatch({
              type: 'updateFileSelectionUploadState',
              payload: {
                id: file.id,
                newState: 'isFetchingMetadata',
              },
            });
            setAIMetadataExtractionStatus(file.id, 'LOADING');

            try {
              const extractedMetaData = await fetchAIExtractedMetadata(file);

              const editorDataToSet: Record<string, unknown> = {};
              const aiExtractionExplanations: Record<
                string,
                string | undefined
              > = {};
              // Update file metadata with the response
              if (
                extractedMetaData &&
                Object.keys(extractedMetaData).length > 0 &&
                schema?.properties
              ) {
                for (const [key, property] of Object.entries(
                  schema.properties
                )) {
                  if (
                    typeof property === 'object' &&
                    typeof property.options?.id === 'string' &&
                    extractedMetaData[property.options.id]
                  ) {
                    editorDataToSet[key] =
                      extractedMetaData[property.options.id].value;
                    aiExtractionExplanations[key] =
                      extractedMetaData[property.options.id].explanation;
                  }
                }
                editorDataChangeHandler(file.id, editorDataToSet);
                setAiExtractionExplanations(file.id, aiExtractionExplanations);
              }
              setAIMetadataExtractionStatus(file.id, 'SUCCESS');
            } catch (error) {
              console.error(
                `Failed to extract AI metadata for file ${file.file.name}:`,
                error
              );
              setAIMetadataExtractionStatus(file.id, 'FAILED');
            } finally {
              // Reset the file state to initial for both success and error condition
              fileStateDispatch({
                type: 'updateFileSelectionUploadState',
                payload: {
                  id: file.id,
                  newState: 'initial',
                },
              });
            }
          })
        );
      }
    },
    [
      contentId,
      fileStateDispatch,
      fileState,
      queryClient,
      setAIMetadataExtractionStatus,
    ]
  );

  return { fetchAIMetadataForAllFiles };
};
